// /host/events/new — host posts a new event (draft).
//
// Form mirrors createEventInputSchema field-for-field. Date+time inputs are
// kept simple (HTML datetime-local) — they round-trip to UTC ms via Date.

import {
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ArtFormGroup } from '@wwaave/shared/vocab';
import { useEffect, useState } from 'react';

import { AppShell } from '../../../components/app-shell/AppShell';
import { csrfHeaders } from '../../../lib/auth-context';
import { useFormTelemetry } from '../../../lib/telemetry-form';
import { showToast } from '../../../lib/toast';

export const Route = createFileRoute('/host/events/new')({
  component: HostEventNewPage,
});

type VenueRow = {
  id: string;
  name: string;
  city: string;
  status: 'draft' | 'pending_review' | 'live' | 'paused' | 'removed';
};

type ArtFormRow = {
  id: string;
  slug: string;
  name: string;
  group: ArtFormGroup;
};

type FormValues = {
  venue_id: string;
  art_form_id: string;
  title: string;
  description: string;
  starts_at_local: string; // datetime-local string
  ends_at_local: string;
  slots_total: number | '';
  pay_dollars: number | '';
  pay_notes: string;
};

const INITIAL_VALUES: FormValues = {
  venue_id: '',
  art_form_id: '',
  title: '',
  description: '',
  starts_at_local: '',
  ends_at_local: '',
  slots_total: 1,
  pay_dollars: '',
  pay_notes: '',
};

function HostEventNewPage() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<VenueRow[] | null>(null);
  const [artForms, setArtForms] = useState<ArtFormRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/venues/me/venues', { credentials: 'include' });
        const json = (await res.json()) as {
          ok: boolean;
          venues?: VenueRow[];
          error?: { message?: string };
        };
        if (cancelled) return;
        if (!res.ok || !json.ok) {
          setLoadError(json.error?.message ?? "Couldn't load your venues.");
          return;
        }
        setVenues(json.venues ?? []);
      } catch {
        if (!cancelled) setLoadError('Network problem loading your venues.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const form = useForm<FormValues>({
    initialValues: INITIAL_VALUES,
    validate: {
      venue_id: (v) => (v.length === 0 ? 'Pick one of your venues.' : null),
      title: (v) => (v.trim().length < 3 ? 'Give it a short title.' : null),
      starts_at_local: (v) => (v.length === 0 ? 'When does it start?' : null),
      ends_at_local: (v, all) => {
        if (v.length === 0) return 'When does it end?';
        if (all.starts_at_local.length > 0 && new Date(v) < new Date(all.starts_at_local)) {
          return 'End must be after start.';
        }
        return null;
      },
      slots_total: (v) => (typeof v !== 'number' || v < 1 ? 'At least one slot.' : null),
    },
  });

  const tel = useFormTelemetry('host.event-create', form);

  // Load art forms whenever the picked venue changes. We narrow the picker to
  // the venue's accepted art forms so the host stays inside what they've
  // already programmed for. Default the picker to the first one to preserve
  // the pre-Wave-4M behaviour where the spawned exchange used venue art
  // forms[0].
  const selectedVenueId = form.values.venue_id;
  useEffect(() => {
    if (selectedVenueId.length === 0) {
      setArtForms(null);
      form.setFieldValue('art_form_id', '');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/events/art-forms?venue_id=${encodeURIComponent(selectedVenueId)}`,
          { credentials: 'include' },
        );
        const json = (await res.json()) as {
          ok: boolean;
          items?: ArtFormRow[];
          error?: { message?: string };
        };
        if (cancelled) return;
        if (res.ok && json.ok && Array.isArray(json.items)) {
          setArtForms(json.items);
          // Default to the first art form on the venue. Preserves pre-Wave-4M
          // behaviour where the accept handler picked venue_art_forms[0].
          if (json.items.length > 0) {
            const first = json.items[0];
            if (first !== undefined) {
              form.setFieldValue('art_form_id', first.id);
            }
          } else {
            form.setFieldValue('art_form_id', '');
          }
        }
      } catch {
        // Swallow — the picker is optional, the field is optional too.
      }
    })();
    return () => {
      cancelled = true;
    };
    // form ref isn't stable per Mantine; we intentionally only react to
    // venue id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenueId]);

  async function onSubmit(values: FormValues): Promise<void> {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const startsMs = new Date(values.starts_at_local).getTime();
      const endsMs = new Date(values.ends_at_local).getTime();
      if (!Number.isFinite(startsMs) || !Number.isFinite(endsMs)) {
        setSubmitError('Those dates didn\'t parse. Pick again.');
        return;
      }
      const payCents =
        typeof values.pay_dollars === 'number' && values.pay_dollars > 0
          ? Math.round(values.pay_dollars * 100)
          : undefined;
      const body: Record<string, unknown> = {
        venue_id: values.venue_id,
        title: values.title.trim(),
        starts_at: startsMs,
        ends_at: endsMs,
        slots_total: typeof values.slots_total === 'number' ? values.slots_total : 1,
      };
      if (values.description.trim().length > 0) body['description'] = values.description.trim();
      if (values.art_form_id.length > 0) body['art_form_id'] = values.art_form_id;
      if (payCents !== undefined) body['pay_cents'] = payCents;
      if (values.pay_notes.trim().length > 0) body['pay_notes'] = values.pay_notes.trim();

      // No trailing slash — Hono doesn't match `/api/events/` against the mounted
      // route and it falls through to app.notFound's API 404 guard.
      const res = await fetch('/api/events', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as {
        ok: boolean;
        id?: string;
        error?: { message?: string };
      };
      if (!res.ok || !json.ok || json.id === undefined) {
        const msg = json.error?.message ?? "Couldn't save the event.";
        tel.trackSubmitFailure(msg.slice(0, 80) || 'unknown');
        setSubmitError(msg);
        return;
      }
      tel.trackSubmitSuccess();
      showToast({ message: 'Saved as draft.', variant: 'success' });
      navigate({ to: '/host/events/$id/manage', params: { id: json.id } });
    } catch {
      tel.trackSubmitFailure('network');
      setSubmitError('Network problem. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
          <Group justify="space-between" align="flex-end">
            <Stack gap={4}>
              <Title order={1}>Post an event</Title>
              <Text c="dimmed" size="sm">
                Save as a draft now, publish when you're ready.
              </Text>
            </Stack>
            <Button component={Link} to="/host/events" variant="subtle">
              Back to events
            </Button>
          </Group>

          {(() => {
            // CFR-014: events live inside published venues. Narrow the picker
            // to venues with status='live' so a draft / paused / removed
            // venue can't be selected; the backend rejects on publish with a
            // generic 400 otherwise.
            const liveVenues = venues?.filter((v) => v.status === 'live') ?? null;
            if (loadError !== null) return <Text c="red">{loadError}</Text>;
            if (venues === null) return <Text c="dimmed">Loading your venues…</Text>;
            if (venues.length === 0) {
              return (
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    <Text>You'll need a venue first.</Text>
                    <Group>
                      <Button component={Link} to="/host/venues/new">
                        Add a venue
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              );
            }
            if (liveVenues !== null && liveVenues.length === 0) {
              return (
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    <Title order={3}>Publish a venue first</Title>
                    <Text c="dimmed">
                      Events live inside published venues. Once a venue is live, you can post
                      events for it from here.
                    </Text>
                    <Group>
                      <Button component={Link} to="/host/venues">
                        Go to venues
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              );
            }
            return (
            <form
              onSubmit={form.onSubmit((values) => {
                void onSubmit(values);
              })}
            >
              <Stack gap="lg">
                <Paper p="md" withBorder radius="md">
                  <Stack gap="md">
                    <Title order={3}>The basics</Title>
                    <Select
                      label="Venue"
                      description="Only your published venues are listed here."
                      required
                      data={(liveVenues ?? []).map((v) => ({
                        value: v.id,
                        label: `${v.name} · ${v.city}`,
                      }))}
                      {...form.getInputProps('venue_id')}
                    />
                    {artForms !== null && artForms.length > 0 ? (
                      <Select
                        label="Art form"
                        description="What kind of work fills this slot?"
                        data={artForms.map((af) => ({
                          value: af.id,
                          label: af.name,
                        }))}
                        {...form.getInputProps('art_form_id')}
                      />
                    ) : null}
                    <TextInput
                      label="Title"
                      placeholder="Sunday Sessions, singer-songwriter slot"
                      maxLength={200}
                      required
                      {...form.getInputProps('title')}
                    />
                    <Textarea
                      label="Description"
                      description="What's the night like? What kind of artist would suit this room?"
                      autosize
                      minRows={4}
                      maxRows={20}
                      maxLength={8000}
                      {...form.getInputProps('description')}
                    />
                  </Stack>
                </Paper>

                <Paper p="md" withBorder radius="md">
                  <Stack gap="md">
                    <Title order={3}>Date and time</Title>
                    <Group grow>
                      <TextInput
                        type="datetime-local"
                        label="Starts"
                        required
                        {...form.getInputProps('starts_at_local')}
                      />
                      <TextInput
                        type="datetime-local"
                        label="Ends"
                        required
                        {...form.getInputProps('ends_at_local')}
                      />
                    </Group>
                    <NumberInput
                      label="Slots open"
                      description="Most events fill one slot. A weekender might have two."
                      min={1}
                      max={50}
                      required
                      {...form.getInputProps('slots_total')}
                    />
                  </Stack>
                </Paper>

                <Paper p="md" withBorder radius="md">
                  <Stack gap="md">
                    <Title order={3}>Pay</Title>
                    <Text size="sm" c="dimmed">
                      Optional. Leave blank if it's a door split or stipend-only conversation.
                    </Text>
                    <NumberInput
                      label="Guaranteed pay (AUD)"
                      description="The artist sees this before they raise their hand."
                      min={0}
                      max={10_000}
                      decimalScale={0}
                      {...form.getInputProps('pay_dollars')}
                    />
                    <Textarea
                      label="Pay notes"
                      description="Door splits, accommodation, food. Plain language."
                      autosize
                      minRows={2}
                      maxRows={6}
                      maxLength={500}
                      {...form.getInputProps('pay_notes')}
                    />
                  </Stack>
                </Paper>

                {submitError !== null ? <Text c="red">{submitError}</Text> : null}

                <Group justify="flex-end">
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => navigate({ to: '/host/events' })}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={submitting}
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  >
                    Save draft
                  </Button>
                </Group>
              </Stack>
            </form>
            );
          })()}
        </Stack>
    </AppShell>
  );
}
